export type KeywordText = "and" | "or" | "not" | "near" | "never" | "always" | "paid";
export type Keyword = {
  type: KeywordText;
  startPos: number;
  endPos: number;
};
export type ExpressionType = "literal" | "near" | "not" | "and" | "or" | "never" | "always" | "paid";
export type Expression =
  | {
      type: "literal";
      startPos: number;
      endPos: number;
      value: string;
    }
  | {
      type: "paid";
      startPos: number;
      endPos: number;
      keyword: Keyword & { type: "paid" };
    }
  | {
      type: "near";
      startPos: number;
      endPos: number;
      keyword: Keyword & { type: "near" };
      value: Expression & { type: "literal" };
    }
  | {
      type: "not";
      startPos: number;
      endPos: number;
      keyword: Keyword & { type: "not" };
      value: Expression;
    }
  | {
      type: "and";
      startPos: number;
      endPos: number;
      keyword: Keyword & { type: "and" };
      value: [Expression, Expression];
    }
  | {
      type: "or";
      startPos: number;
      endPos: number;
      keyword: Keyword & { type: "or" };
      value: [Expression, Expression];
    }
  | {
      type: "never";
      startPos: number;
      endPos: number;
      keyword: Keyword & { type: "never" };
    }
  | {
      type: "always";
      startPos: number;
      endPos: number;
      keyword: Keyword & { type: "always" };
    };

export type Parser<T> = (input: string, pos: number) => { value: T; pos: number };

export class ParseError extends Error {
  constructor(name: string, public pos: number, extraMessage: string = "") {
    super(`Error parsing ${name} at position ${pos}` + extraMessage);
  }
}

type ParseSeq<T extends any[]> = T extends [infer S, ...infer T] ? [() => Parser<S>, ...ParseSeq<T>] : [];

function gobWhitespace(input: string, pos: number): number {
  while (/\s/.test(input[pos])) {
    pos++;
  }
  return pos;
}

function assertClosed(input: string, pos: number, name: string): void {
  if (input.length !== pos && !/\s|\)/.test(input[pos])) {
    throw new ParseError(name, pos);
  }
}

export function keyword<T extends KeywordText>(str: T): () => Parser<Keyword & { type: T }> {
  return () => (input, pos) => {
    if (input.startsWith(str, pos)) {
      const endPos = pos + str.length;
      assertClosed(input, endPos, "Keyword");
      return { value: { type: str, startPos: pos, endPos }, pos: gobWhitespace(input, endPos) };
    } else {
      throw new ParseError(str, pos);
    }
  };
}

export function openParen(): Parser<void> {
  return (input, pos) => {
    if (input[pos] === "(") {
      return { value: undefined, pos: gobWhitespace(input, pos + 1) };
    } else {
      throw new ParseError("Open Paren", pos);
    }
  };
}
export function closeParen(): Parser<void> {
  return (input, pos) => {
    if (input[pos] === ")") {
      return { value: undefined, pos: gobWhitespace(input, pos + 1) };
    } else {
      throw new ParseError("Close Paren", pos);
    }
  };
}

export function mapP<T, R>(p: () => Parser<T>, fn: (t: T) => R): () => Parser<R> {
  return () => (input, pos) => {
    const res = p()(input, pos);
    return { value: fn(res.value), pos: res.pos };
  };
}

export function literal(): Parser<Expression & { type: "literal" }> {
  return (input, pos) => {
    const startPos = pos;
    if (input[pos] !== '"') {
      throw new ParseError("Literal Expression", pos);
    }
    pos++;
    while (input[pos] !== '"') {
      if (pos === input.length) {
        throw new ParseError("Literal Expression", pos, " - end of input reached");
      }
      if (input[pos] === "\\") {
        pos += 2;
      } else {
        pos++;
      }
    }
    pos++;
    assertClosed(input, pos, "Literal Expression");

    const value = input
      .slice(startPos + 1, pos - 1)
      .replace(/\\"/g, '"')
      .replace(/\\/g, "\\");

    return {
      value: { type: "literal", value, startPos, endPos: pos },
      pos: gobWhitespace(input, pos),
    };
  };
}

export function seq<T, R extends (...args: any[]) => T>(
  name: string,
  reduce: R,
  ...parsers: ParseSeq<Parameters<R>>
): Parser<T> {
  return (input, pos) => {
    let results = [] as unknown as Parameters<R>;
    let nextPos = pos;
    for (const parser of parsers) {
      const res = (parser as any)()(input, nextPos);
      results.push(res.value);
      nextPos = res.pos;
    }
    return { value: reduce(...results), pos: nextPos };
  };
}

export function either<T>(name: string, ...parsers: (() => Parser<T>)[]): Parser<T> {
  return (input, pos) => {
    for (const parser of parsers) {
      try {
        return parser()(input, pos);
      } catch (e) {
        const err = e as ParseError;
        if (err.pos !== pos) {
          throw err;
        }
        continue;
      }
    }
    throw new ParseError(name, pos);
  };
}

export function near(): Parser<Expression & { type: "near" }> {
  return seq<
    Expression & { type: "near" },
    (k: Keyword & { type: "near" }, v: Expression & { type: "literal" }) => Expression & { type: "near" }
  >(
    "Near Expression",
    (keyword, value) => {
      return {
        type: "near",
        startPos: keyword.startPos,
        endPos: value.endPos,
        keyword,
        value,
      };
    },
    keyword("near"),
    literal
  );
}

export function leaf(): Parser<Expression & { type: "never" | "paid" | "always" | "near" | "literal" }> {
  return either<Expression & { type: "never" | "always" | "paid" | "near" | "literal" }>(
    "Leaf Expression",
    mapP(keyword("paid"), (n) => ({
      type: "paid",
      startPos: n.startPos,
      endPos: n.endPos,
      keyword: n,
    })),
    mapP(keyword("always"), (n) => ({
      type: "always",
      startPos: n.startPos,
      endPos: n.endPos,
      keyword: n,
    })),
    mapP(keyword("never"), (n) => ({
      type: "never",
      startPos: n.startPos,
      endPos: n.endPos,
      keyword: n,
    })),
    literal,
    near
  );
}

export function parentheses(): Parser<Expression> {
  return (input, pos) => {
    const open = openParen()(input, pos);
    const expr = expression()(input, open.pos);
    const close = closeParen()(input, expr.pos);
    return { value: expr.value, pos: close.pos };
  };
}

export function not(): Parser<Expression & { type: "not" }> {
  return seq<
    Expression & { type: "not" },
    (k: Keyword & { type: "not" }, v: Expression) => Expression & { type: "not" }
  >(
    "Not Expression",
    (keyword, value) => {
      return {
        type: "not",
        startPos: keyword.startPos,
        endPos: value.endPos,
        keyword,
        value,
      };
    },
    keyword("not"),
    operand
  );
}

export function operand(): Parser<Expression> {
  return either<Expression>("Expression", parentheses, leaf, not);
}

export function expression(): Parser<Expression> {
  return (input, pos) => {
    const left = operand()(input, pos);
    let kwd: {
      value: Keyword & { type: "and" | "or" };
      pos: number;
    };
    try {
      kwd = either<Keyword & { type: "and" | "or" }>("Binop", keyword("and"), keyword("or"))(input, left.pos);
    } catch (e) {
      return left;
    }
    const right = expression()(input, kwd.pos);
    return {
      value: {
        type: kwd.value.type,
        startPos: left.value.startPos,
        endPos: right.value.endPos,
        keyword: kwd.value,
        value: [left.value, right.value],
      } as Expression & { type: "and" | "or" },
      pos: right.pos,
    };
  };
}

export function root(): Parser<Expression> {
  return (input, pos) => {
    pos = gobWhitespace(input, pos);
    const expr = expression()(input, pos);
    if (expr.pos !== input.length) {
      throw new ParseError("End of input", expr.pos);
    }
    return expr;
  };
}
